import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, Settings, Zap, CheckCircle, Award, Search, Globe, Facebook, ShoppingCart, Music2, Linkedin } from 'lucide-react';

const services = [
  {
    title: 'Campaign Setup & Management',
    tagline: 'Launch client campaigns faster, cleaner, and error-free — every time.',
    icon: <Settings className="w-8 h-8 text-[var(--accent-primary)]" />,
    benefits: [
      'Launch client campaigns 2x faster with zero trafficking errors',
      'Accounts structured for scale from day one',
      'Pixel-perfect conversion tracking from first go-live',
      'Smarter bid structures and pacing that protect client budgets'
    ],
    features: [
      'Campaign architecture & structure design',
      'Conversion tracking setup & QA',
      'Custom audience creation & segmentation',
      'Bid strategy design & optimisation',
      'Budget allocation & pacing control',
      'Ad copy & creative trafficking'
    ],
    altBg: false
  },
  {
    title: 'Performance Monitoring & Optimisation',
    tagline: 'Daily hands-on management that compounds results across your client portfolio.',
    icon: <BarChart3 className="w-8 h-8 text-[var(--accent-primary)]" />,
    benefits: [
      'Average 35% lift in campaign performance within 90 days',
      'Always-on monitoring — issues caught before they impact spend',
      'Faster issue detection before client ROI takes a hit',
      'Consistent optimisations that build compounding results'
    ],
    features: [
      'Real-time performance monitoring',
      'Automated bid & budget adjustments',
      'Keyword & negative keyword management',
      'Audience refinement & lookalike expansion',
      'Quality Score improvement initiatives',
      'Weekly white-label optimisation reports'
    ],
    altBg: true
  },
  {
    title: 'Platform Troubleshooting & Support',
    tagline: 'When platforms break, policies change, or accounts act up — we fix it fast.',
    icon: <Zap className="w-8 h-8 text-[var(--accent-primary)]" />,
    benefits: [
      '99.8% uptime with rapid technical issue resolution',
      'Stay ahead of platform policy changes — no surprise suspensions',
      "Protect your clients' accounts from disapprovals and bans",
      'Continuous platform health monitoring across all accounts'
    ],
    features: [
      'Technical issue diagnosis & resolution',
      'Policy compliance audits & remediation',
      'Integration troubleshooting (CRM, pixels, feeds)',
      'Custom solution development',
      'Platform relationship management',
      'Account suspension prevention & recovery'
    ],
    altBg: false
  }
];

const platforms = [
  { platform: 'Google Ads', specialty: 'Search, Shopping, YouTube, Display', certification: 'Google Certified Partner', icon: <Search className="w-6 h-6 text-[var(--accent-primary)]" /> },
  { platform: 'Microsoft Advertising', specialty: 'Search, Bing Ads, Audience Network', certification: 'Microsoft Accredited', icon: <Globe className="w-6 h-6 text-[var(--accent-primary)]" /> },
  { platform: 'Meta Ads', specialty: 'Facebook, Instagram, Reels', certification: 'Meta Partner Certified', icon: <Facebook className="w-6 h-6 text-[var(--accent-primary)]" /> },
  { platform: 'Amazon Ads', specialty: 'Sponsored Products, DSP, Attribution', certification: 'Amazon Ads Certified', icon: <ShoppingCart className="w-6 h-6 text-[var(--accent-primary)]" /> },
  { platform: 'TikTok Ads', specialty: 'In-Feed, Branded Content, TopView', certification: 'TikTok Partner Certified', icon: <Music2 className="w-6 h-6 text-[var(--accent-primary)]" /> },
  { platform: 'LinkedIn Ads', specialty: 'Sponsored Content, Dynamic Ads, InMail', certification: 'LinkedIn Certified', icon: <Linkedin className="w-6 h-6 text-[var(--accent-primary)]" /> }
];

const processSteps = [
  {
    step: '1',
    title: 'Account Audit & Analysis',
    deliverables: 'White-label performance audit report, optimisation roadmap, account health scorecard — ready to share with your client or keep internal.'
  },
  {
    step: '2',
    title: 'Strategic Planning & Setup',
    deliverables: "Campaign restructure plan, tracking QA, account organisation — built to your agency's naming conventions and reporting standards."
  },
  {
    step: '3',
    title: 'Launch & Optimisation',
    deliverables: 'Campaign launch confirmation, real-time monitoring dashboards, weekly white-label optimisation reports delivered under your brand.'
  },
  {
    step: '4',
    title: 'Ongoing Management & Growth',
    deliverables: 'Monthly performance reviews, quarterly strategy updates, annual account planning sessions — your ops team, your client relationship.'
  }
];

const results = [
  { metric: '35%', label: 'Average campaign performance lift within 90 days of MarqiOps onboarding' },
  { metric: '99.8%', label: 'Platform uptime reliability across all managed accounts' },
  { metric: '60%', label: 'Reduction in time your internal team spends on execution and troubleshooting' },
  { metric: '48 hrs', label: 'Average time from signed agreement to your team being operational' }
];

const AdOperationsPage = () => {
  return (
    <div className="min-h-screen py-20" data-testid="ad-operations-page">
      <div className="container">
        {/* Hero Section */}
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h1 className="display-md text-[var(--text-primary)] mb-6" data-testid="adops-hero-title">
            Your Clients' Campaigns Run Perfectly. Every Time. Under Your Brand.
          </h1>
          <p className="body-lg mb-12">
            Ad ops errors cost agencies clients. Slow trafficking kills margins. Missed optimizations
            erode trust. MarqiOps gives your agency a white-label ad operations team that handles the
            full execution layer — so your team can focus on relationships, strategy, and growth.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/contact" className="btn-primary" data-testid="adops-hero-cta-primary">
              Get a Free Ad Ops Audit
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link to="/ops/pricing" className="btn-secondary" data-testid="adops-hero-cta-secondary">
              View Pricing Plans
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>

        {/* Comprehensive Ad Ops Services Overview */}
        <section className="mb-16">
          <div className="text-center mb-16">
            <h2 className="h1 text-[var(--text-primary)] mb-6">
              Everything Your Team Shouldn't Be Doing Themselves.
            </h2>
            <p className="body-lg max-w-3xl mx-auto">
              From first trafficking to ongoing optimisation, your dedicated MarqiOps specialists handle
              every execution detail — across every platform your clients use — with the accuracy and
              speed your agency reputation depends on.
            </p>
          </div>
        </section>

        {/* Main Services */}
        <section className="mb-24">
          <div className="space-y-16">
            {services.map((service) => (
              <div key={service.title} className={`feature-card ${service.altBg ? 'bg-[var(--bg-secondary)]' : ''}`}>
                <div className="grid lg:grid-cols-2 gap-12 items-start">
                  <div>
                    <div className="flex items-center mb-4">
                      <div className="w-16 h-16 bg-[var(--accent-bg)] rounded-2xl flex items-center justify-center mr-6">
                        {service.icon}
                      </div>
                      <h3 className="h1 text-[var(--text-primary)]">{service.title}</h3>
                    </div>
                    <p className="body-lg mb-8">{service.tagline}</p>

                    <div>
                      <h4 className="h3 text-[var(--text-primary)] mb-4">Key Benefits for Your Agency:</h4>
                      <div className="grid grid-cols-1 gap-3">
                        {service.benefits.map((benefit) => (
                          <div key={benefit} className="flex items-start">
                            <CheckCircle className="w-5 h-5 text-[var(--accent-primary)] mr-3 flex-shrink-0 mt-0.5" />
                            <span className="text-[var(--text-secondary)]">{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="h3 text-[var(--text-primary)] mb-6">What We Handle:</h4>
                    <div className="space-y-4">
                      {service.features.map((feature) => (
                        <div key={feature} className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-[var(--accent-primary)] rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-[var(--text-secondary)]">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Multi-Platform Expertise */}
        <section className="mb-24 bg-[var(--bg-secondary)] -mx-6 px-6 py-16 rounded-2xl">
          <div className="text-center mb-16">
            <h2 className="h1 text-[var(--text-primary)] mb-6">
              Certified Across Every Platform Your Clients Use.
            </h2>
            <p className="body-lg max-w-3xl mx-auto">
              Your clients don't live on one platform — and neither do we. MarqiOps specialists hold
              active certifications across all major US advertising platforms, so your agency can take
              on any brief with confidence.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {platforms.map((item) => (
              <div key={item.platform} className="group">
                <div className="p-6 bg-[var(--bg-primary)] rounded-lg border border-[var(--border-subtle)] hover:border-[var(--accent-primary)] transition-all duration-300 group-hover:transform group-hover:scale-105">
                  <div className="flex items-center mb-3">
                    <span className="mr-3">{item.icon}</span>
                    <div>
                      <h3 className="h3 text-[var(--text-primary)]">{item.platform}</h3>
                      <p className="text-sm text-[var(--text-secondary)]">{item.specialty}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Award className="w-4 h-4 text-[var(--accent-primary)] mr-2" />
                    <span className="text-[var(--accent-primary)] font-semibold text-sm">{item.certification}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Our Ad Ops Process */}
        <section className="mb-24">
          <div className="text-center mb-16">
            <h2 className="h1 text-[var(--text-primary)] mb-6">
              A 4-Step System Built for Agency Client Delivery.
            </h2>
            <p className="body-lg max-w-3xl mx-auto">
              Every new client engagement follows the same proven process — so you get consistent,
              predictable execution across your entire portfolio, not just your biggest accounts.
            </p>
          </div>

          <div className="space-y-8">
            {processSteps.map((process) => (
              <div key={process.step} className="flex flex-col md:flex-row gap-8 items-start">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-[var(--accent-primary)] rounded-full flex items-center justify-center">
                    <span className="text-[var(--bg-primary)] font-bold text-lg">{process.step}</span>
                  </div>
                </div>
                <div className="feature-card flex-1">
                  <h3 className="h2 text-[var(--text-primary)] mb-4">{process.title}</h3>
                  <p className="text-[var(--text-secondary)]">
                    <strong>Deliverables:</strong> {process.deliverables}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Performance Results */}
        <section className="mb-24">
          <div className="text-center mb-16">
            <h2 className="h1 text-[var(--text-primary)] mb-6">
              Numbers Your Clients Will Ask About.
            </h2>
            <p className="body-lg max-w-3xl mx-auto">
              These aren't projections — they're averages across active agency client accounts managed
              by MarqiOps specialists.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {results.map((stat, index) => (
              <div key={stat.label} className="text-center animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="h1 text-[var(--accent-primary)] mb-4">{stat.metric}</div>
                <div className="text-[var(--text-muted)]">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="text-center">
          <div className="feature-card bg-[var(--accent-bg)]">
            <h2 className="h1 text-[var(--text-primary)] mb-6">
              Ready to Deliver Flawless Ad Ops — Without Expanding Your Team?
            </h2>
            <p className="body-lg mb-12 max-w-3xl mx-auto">
              Your next client campaign shouldn't be a staffing problem. MarqiOps plugs into your agency
              in 48 hours, operates under your brand, and handles everything from trafficking to
              optimisation — so you can win more clients and actually deliver.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact" className="btn-primary glow-effect" data-testid="adops-final-cta-primary">
                Book a Free Ad Ops Audit
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link to="/ops/pricing" className="btn-secondary" data-testid="adops-final-cta-secondary">
                Explore Pricing Plans
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdOperationsPage;
